import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Send } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { QuotationDialog, openQuotationPdf } from "@/components/admin/QuotationDialog";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];

export function QuotesPanel() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(null);
  const [sending, setSending] = useState("");

  useEffect(() => {
    adminApi.get("/admin/quotes").then(({ data }) => setQuotes(data)).catch(() => toast.error("Failed to load quotes")).finally(() => setLoading(false));
  }, []);

  const setStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    } catch {
      toast.error("Could not update status");
    }
  };

  const onCreated = (qt) => {
    setQuotes((qs) => qs.map((q) => (q.id === qt.quote_id ? { ...q, status: "quoted", quotation_id: qt.id, quotation_number: qt.number } : q)));
    setBuilding(null);
    openQuotationPdf(qt.id);
  };

  const send = async (q) => {
    if (!window.confirm(`Email quotation ${q.quotation_number} to ${q.email}?`)) return;
    setSending(q.id);
    try {
      const { data } = await adminApi.post(`/admin/quotations/${q.quotation_id}/send`);
      if (data.sent_customer) {
        toast.success(`Quotation emailed to ${q.email}`);
        setQuotes((qs) => qs.map((x) => (x.id === q.id ? { ...x, quotation_sent: true } : x)));
      } else {
        toast.error(`Customer email failed${data.sent_copy ? " (your copy was sent to info@)" : ""}. ${data.error || ""}`.trim(), { duration: 9000 });
      }
    } catch {
      toast.error("Could not send the quotation");
    } finally {
      setSending("");
    }
  };

  return (
    <div className="bg-white border border-brand/10 rounded-2xl overflow-hidden" data-testid="quotes-panel">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="quotes-table">
          <thead className="bg-brand-50/60 text-brand text-left">
            <tr>{["Ref", "Customer", "Items", "Location", "Notes", "Status", "Date", "Quotation"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {quotes.map((q) => (
              <tr key={q.id} className="border-t border-brand/5 align-top" data-testid="quote-row">
                <td className="px-4 py-3 font-mono text-xs">{q.ref}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{q.name}</div>
                  {q.company && <div className="text-muted-foreground">{q.company}</div>}
                  <a href={`mailto:${q.email}`} className="text-brand hover:underline block">{q.email}</a>
                  {q.phone && <a href={`tel:${q.phone}`} className="text-muted-foreground block">{q.phone}</a>}
                </td>
                <td className="px-4 py-3">
                  {(q.items || []).map((i) => <div key={i.name}><b>{i.quantity}×</b> {i.name}</div>)}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{q.location || "-"}</td>
                <td className="px-4 py-3 text-muted-foreground max-w-xs whitespace-pre-wrap">{q.notes || "-"}</td>
                <td className="px-4 py-3">
                  <select value={q.status || "new"} onChange={(e) => setStatus(q.id, e.target.value)} data-testid={`quote-status-${q.ref}`} className="h-9 rounded-md border border-input bg-background px-2 text-sm capitalize">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{new Date(q.ts).toLocaleString()}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {q.quotation_id ? (
                    <div className="flex flex-col gap-1.5">
                      <span className="font-mono text-xs">{q.quotation_number}{q.quotation_sent ? " · sent" : ""}</span>
                      <div className="flex gap-1.5">
                        <button onClick={() => openQuotationPdf(q.quotation_id)} data-testid={`quote-pdf-${q.ref}`} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-brand-50"><FileText className="h-3.5 w-3.5" />PDF</button>
                        <button onClick={() => send(q)} disabled={sending === q.id} data-testid={`quote-send-${q.ref}`} className="inline-flex items-center gap-1 rounded-md bg-brand px-2 py-1 text-xs text-white hover:bg-brand-700 disabled:opacity-60"><Send className="h-3.5 w-3.5" />{sending === q.id ? "Sending…" : "Email"}</button>
                      </div>
                      <button onClick={() => setBuilding(q)} className="text-left text-xs text-muted-foreground hover:underline">New version</button>
                    </div>
                  ) : (
                    <button onClick={() => setBuilding(q)} data-testid={`quote-build-${q.ref}`} className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-xs text-white hover:bg-brand-700"><FileText className="h-3.5 w-3.5" />Create quotation</button>
                  )}
                </td>
              </tr>
            ))}
            {loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!quotes.length && !loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="quotes-empty">No quote requests yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <QuotationDialog quote={building} onClose={() => setBuilding(null)} onCreated={onCreated} />
    </div>
  );
}
